"use client";

import { SCREENS } from "@tami/tokens";
import { createContext, useContext, useSyncExternalStore } from "react";

export type DemoViewportMode = "mobile" | "tablet" | "desktop";

export type DemoViewportContextValue = {
  mode: DemoViewportMode;
  /** Sidebar visível só no layout desktop (como no app autenticado). */
  showSidebar: boolean;
  /** Largura útil do frame da demo (px). */
  frameWidth: number;
};

export type DemoShellSpec = {
  width: number;
  height: number;
};

/**
 * Resolução lógica de cada device — é nela que o app replicado é montado, e a
 * tela inteira é reduzida por transform até caber no frame. Mobile e tablet são
 * medidas reais (iPhone 14, iPad Air 11").
 *
 * O desktop é 16/9 de propósito, e não a proporção real de um MacBook (1.54):
 * a tela de MacBook deixa o notebook tão alto que ele não cabe na página junto
 * com o seletor e a fileira de abas, e aí some metade do que a demo quer
 * mostrar. 1600×900 é resolução de desktop de verdade e dá espaço de sobra ao
 * layout — o que estava errado antes era montar o app em ~1170px, apertando um
 * layout desenhado para telas largas.
 */
export const DEMO_SHELL_SPECS: Record<DemoViewportMode, DemoShellSpec> = {
  mobile: { width: 390, height: 844 },
  tablet: { width: 834, height: 1194 },
  desktop: { width: 1600, height: 900 },
};

const DEFAULT_VIEWPORT: DemoViewportContextValue = {
  mode: "desktop",
  showSidebar: true,
  frameWidth: 1200,
};

export const DemoViewportContext =
  createContext<DemoViewportContextValue>(DEFAULT_VIEWPORT);

export function useDemoViewport(): DemoViewportContextValue {
  return useContext(DemoViewportContext);
}

export function resolveDemoViewport(
  mode: DemoViewportMode,
  frameWidth: number,
): DemoViewportContextValue {
  return {
    mode,
    showSidebar: mode === "desktop",
    frameWidth,
  };
}

const MOBILE_BREAKPOINT_PX = Number.parseInt(SCREENS.md, 10) - 1;
const TABLET_BREAKPOINT_PX = Number.parseInt(SCREENS.lg, 10) - 1;
const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT_PX}px)`;
const TABLET_MEDIA_QUERY = `(min-width: ${SCREENS.md}) and (max-width: ${TABLET_BREAKPOINT_PX}px)`;
const DESKTOP_MEDIA_QUERY = `(min-width: ${SCREENS.lg})`;

const getLandingFrameMode = (): DemoViewportMode => {
  if (window.matchMedia(DESKTOP_MEDIA_QUERY).matches) return "desktop";
  if (window.matchMedia(TABLET_MEDIA_QUERY).matches) return "tablet";
  return "mobile";
};

const subscribeLandingFrameMode = (onStoreChange: () => void): (() => void) => {
  const desktopQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
  const tabletQuery = window.matchMedia(TABLET_MEDIA_QUERY);
  const mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
  desktopQuery.addEventListener("change", onStoreChange);
  tabletQuery.addEventListener("change", onStoreChange);
  mobileQuery.addEventListener("change", onStoreChange);
  return () => {
    desktopQuery.removeEventListener("change", onStoreChange);
    tabletQuery.removeEventListener("change", onStoreChange);
    mobileQuery.removeEventListener("change", onStoreChange);
  };
};

export type DemoFrameContextValue = {
  mode: DemoViewportMode;
  shell: DemoShellSpec;
};

const DEFAULT_FRAME: DemoFrameContextValue = {
  mode: "desktop",
  shell: DEMO_SHELL_SPECS.desktop,
};

export const DemoFrameContext = createContext<DemoFrameContextValue>(DEFAULT_FRAME);

export function useDemoFrame(): DemoFrameContextValue {
  return useContext(DemoFrameContext);
}

/** Breakpoint da landing: qual formato de device frame usar. */
export function useLandingFrameMode(): DemoViewportMode {
  return useSyncExternalStore(
    subscribeLandingFrameMode,
    getLandingFrameMode,
    () => "desktop",
  );
}
